import { COOKIE_EXPIRES_IN } from '@constants/cookies';
import { getCookie, setCookie } from '@services/cookies';

const SHOPIFY_TRACKING_PARAMS_COOKIE = 'shopifyTrackingParams';

const EXTRA_TRACKING_PARAMS = new Set([
  'awc',
  'gclid',
  'fbclid',
  'msclkid',
  'ttclid',
  'twclid',
  'dclid',
  'gbraid',
  'wbraid',
  'irclickid',
]);

const shouldPersistParam = (key: string) => key.startsWith('utm_') || EXTRA_TRACKING_PARAMS.has(key);

const getStoredTrackingParams = (): Record<string, string> => {
  if (typeof window === 'undefined') {
    return {};
  }

  const rawValue = getCookie(SHOPIFY_TRACKING_PARAMS_COOKIE);
  if (!rawValue) return {};

  try {
    if (typeof rawValue === 'string') {
      return JSON.parse(rawValue);
    }
    return rawValue;
  } catch {
    return {};
  }
};

export const persistTrackingParamsFromSearch = (search = '') => {
  if (typeof window === 'undefined') {
    return;
  }

  const params = new URLSearchParams(search);
  const stored = getStoredTrackingParams();
  let hasChanges = false;

  params.forEach((value, key) => {
    if (!value || !shouldPersistParam(key)) return;
    if (stored[key] !== value) {
      stored[key] = value;
      hasChanges = true;
    }
  });

  if (hasChanges) {
    setCookie(SHOPIFY_TRACKING_PARAMS_COOKIE, JSON.stringify(stored), COOKIE_EXPIRES_IN.thirtyDays);
  }
};

export const appendStoredTrackingParamsToSearch = (search = '') => {
  const mergedParams = new URLSearchParams(search);
  const stored = getStoredTrackingParams();

  Object.entries(stored).forEach(([key, value]) => {
    if (!value || mergedParams.has(key)) return;
    mergedParams.set(key, value);
  });

  const merged = mergedParams.toString();
  return merged ? `?${merged}` : '';
};

export const appendStoredTrackingParamsToUrl = (url: string) => {
  try {
    const parsedUrl = new URL(url);
    const mergedParams = new URLSearchParams(parsedUrl.search);
    const stored = getStoredTrackingParams();

    Object.entries(stored).forEach(([key, value]) => {
      if (!value || mergedParams.has(key)) return;
      mergedParams.set(key, value);
    });

    parsedUrl.search = mergedParams.toString();
    return parsedUrl.toString();
  } catch {
    return url;
  }
};

export const appendShopifyAttributesToUrl = (url: string, attributes: Record<string, string>) => {
  try {
    const parsedUrl = new URL(url);
    const params = new URLSearchParams(parsedUrl.search);

    Object.entries(attributes).forEach(([key, value]) => {
      if (!key || !value) return;
      params.set(`attributes[${key}]`, value);
    });

    parsedUrl.search = params.toString();
    return parsedUrl.toString();
  } catch {
    return url;
  }
};
